import {
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { format } from 'date-fns';
import { CarLicenseEnum } from 'server/shared/models/carLicense.enum';
import { EmploymentInvitee } from 'server/shared/models/employmentInvitee';
import { Invitee, InviteeFileReference } from 'server/shared/models/invitee';
import { JobRequestInvitee } from 'server/shared/models/job-request';
import { LanguageEnum } from 'server/shared/models/language.enum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { SexEnum } from 'server/shared/models/sex.enum';
import { PoolerUser, User } from 'server/shared/models/user';
import {
  UploadFile,
  UserCustomerInformation,
} from 'server/shared/models/userCustomerInformation';
import { downloadFile } from 'src/helpers/downloadFile';
import { PoolerService } from '../../services/pooler/pooler.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';

import { ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { CustomerService } from 'src/app/services/customer.service';
import { UserService } from 'src/app/services/user.service';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';

@Component({
  selector: 'user-list-item-details',
  templateUrl: 'user-list-item-details.component.html',
  styleUrls: ['user-list-item-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserListItemDetailsComponent implements OnDestroy {
  carLicenseEnum = CarLicenseEnum;
  languageEnum = LanguageEnum;
  sexEnum = SexEnum;
  i18n = this.translationService.getTranslations('user-list-item');

  // Loading state observables
  isDownloading$ = new BehaviorSubject<boolean>(false);
  progressbarValue$ = new BehaviorSubject<number>(0);

  loggedInUser$ = this.userService.readyDispoOrRequester$;

  roleEnum = RoleEnum;

  noPadding$ = new BehaviorSubject<boolean>(false);

  @Input() set noPadding(noPadding: boolean) {
    this.noPadding$.next(noPadding);
  }
  get noPadding() {
    return this.noPadding$.value;
  }

  private userInput$ = new BehaviorSubject<User | undefined>(undefined);
  user$ = this.userInput$.pipe(rxFilterIsNotEmpty());

  @Input() set user(user: User | undefined) {
    this.userInput$.next(user);
  }
  get user() {
    return this.userInput$.value;
  }

  private inviteeInput$ = new BehaviorSubject<
    Invitee | EmploymentInvitee | JobRequestInvitee | undefined
  >(undefined);
  invitee$ = this.inviteeInput$.asObservable();

  @Input() set invitee(
    invitee: Invitee | EmploymentInvitee | JobRequestInvitee | undefined,
  ) {
    this.inviteeInput$.next(invitee);
  }
  get invitee() {
    return this.inviteeInput$.value;
  }

  private showContactInfoInput$ = new BehaviorSubject<boolean>(true);
  showContactInfo$ = this.showContactInfoInput$.asObservable();

  @Input() set showContactInfo(showContactInfo: boolean) {
    this.showContactInfoInput$.next(showContactInfo);
  }
  get showContactInfo() {
    return this.showContactInfoInput$.value;
  }

  // Customer information observable
  customerInformation$ = combineLatest([
    this.user$.pipe(startWith(undefined)),
    this.invitee$,
    this.loggedInUser$,
  ]).pipe(
    switchMap(async ([user, invitee, loggedInUser]) => {
      if (user?._id && user.role === RoleEnum.POOL) {
        const customerOfPooler = await this.poolerService.getCustomerOfPooler(
          user._id,
        );

        let customerInformation: UserCustomerInformation | undefined;
        if (customerOfPooler) {
          customerInformation =
            user.customerInformation?.[customerOfPooler._id];
        } else if (invitee) {
          customerInformation = user.customerInformation?.[invitee.customer.id];
        }

        // Filter out tags if not the logged-in user's customer
        if (
          customerInformation?.tags &&
          (customerOfPooler?._id !== loggedInUser?.customerId ||
            !customerOfPooler)
        ) {
          delete customerInformation.tags;
        }

        return customerInformation;
      }
      return undefined;
    }),
    shareReplay(1),
  );

  // Detail list observable
  detailList$ = combineLatest([
    this.user$.pipe(startWith(undefined)),
    this.invitee$,
    this.showContactInfo$,
    this.customerInformation$,
    this.customerService.readyDispoCustomer$,
  ]).pipe(
    map(([user, invitee, showContactInfo, customerInformation, customer]) => {
      const detailList: { label: string; value: string; icon: string }[] = [];

      if (!user && !invitee) return detailList;

      if (showContactInfo) {
        detailList.push({
          label: 'email',
          value: user?.email || invitee?.personalData.email || '',
          icon: 'email-outline',
        });
        if (user?.phone || invitee?.personalData.phone) {
          detailList.push({
            label: 'phone',
            value: user?.phone || invitee?.personalData.phone || '',
            icon: 'call',
          });
        }
      }

      if (
        user?.sex === SexEnum.MALE ||
        invitee?.personalData.sex === SexEnum.MALE
      ) {
        detailList.push({
          label: 'sex',
          value: this.i18n.maleSex || 'male',
          icon: 'human-male-female',
        });
      } else {
        detailList.push({
          label: 'sex',
          value: this.i18n.femaleSex || 'female',
          icon: 'human-male-female',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser).languages &&
        !customer?.hiddenFields?.profileLanguages
      ) {
        let languageString = '';
        (user as PoolerUser).languages?.forEach((language, index) => {
          languageString +=
            (index > 0 ? ', ' : '') +
            (this.i18n.languageEnum?.[language]
              ? this.i18n.languageEnum?.[language]
              : LanguageEnum[language]);
        });
        detailList.push({
          label: 'languages',
          value: languageString,
          icon: 'language',
        });
      }

      if (
        customerInformation?.internalId &&
        !customer?.hiddenFields?.profileInternalId
      ) {
        detailList.push({
          label: 'internalId',
          value: customerInformation.internalId,
          icon: 'badge',
        });
      }

      if (
        customerInformation?.hourlyWage &&
        !customer?.hiddenFields?.profileHourlyWage
      ) {
        detailList.push({
          label: 'hourlyWage',
          value: customerInformation.hourlyWage.toString(),
          icon: 'currency-usd',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.clothingSize &&
        !customer?.hiddenFields?.profileClothingSize
      ) {
        detailList.push({
          label: 'clothingSize',
          value: (user as PoolerUser).clothingSize || '',
          icon: 'laundry',
        });
      }

      if (
        !customer?.hiddenFields?.profileDateOfBirth &&
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.dateOfBirth
      ) {
        detailList.push({
          label: 'date-of-birth',
          value: format(
            new Date((user as PoolerUser).dateOfBirth!),
            'dd.MM.yyyy',
          ),
          icon: 'cake',
        });
      } else if (
        !customer?.hiddenFields?.profileDateOfBirth &&
        invitee?.personalData?.dateOfBirth
      ) {
        detailList.push({
          label: 'date-of-birth',
          value: format(
            new Date(invitee.personalData.dateOfBirth),
            'dd.MM.yyyy',
          ),
          icon: 'cake',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.height &&
        !customer?.hiddenFields?.profileHeight
      ) {
        detailList.push({
          label: 'height',
          value: (user as PoolerUser).height + ' cm',
          icon: 'height',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.itKnowledge &&
        !customer?.hiddenFields?.profileITKnowledge
      ) {
        detailList.push({
          label: 'itKnowledge',
          value: (user as PoolerUser).itKnowledge || '',
          icon: 'laptop',
        });
      }

      if (customerInformation?.monthlyHours) {
        detailList.push({
          label: 'monthlyHours',
          value: customerInformation.monthlyHours.toString(),
          icon: 'clock_loader',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.car &&
        !customer?.hiddenFields?.profileMobility
      ) {
        const carLicense = (user as PoolerUser).car!;
        detailList.push({
          label: 'car',
          value:
            this.i18n.carLicenseEnum?.[carLicense] ||
            CarLicenseEnum[carLicense],
          icon: 'car-outline',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        ((user as PoolerUser).placeOfResidence?.zip ||
          (user as PoolerUser).placeOfResidence?.location) &&
        !customer?.hiddenFields?.profilePlaceOfResidence
      ) {
        const placeOfResidence = (user as PoolerUser).placeOfResidence!;
        detailList.push({
          label: 'place-of-residence',
          value: placeOfResidence.zip + ', ' + placeOfResidence.location,
          icon: 'home',
        });
      }

      if (
        customerInformation?.internalNote &&
        !customer?.hiddenFields?.profileNotes
      ) {
        detailList.push({
          label: 'note',
          value: customerInformation.internalNote,
          icon: 'message-bulleted',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.certificates?.length
      ) {
        detailList.push({
          label: 'certificates',
          value: (user as PoolerUser)
            .certificates!.map((c) => c.displayName)
            .join(', '),
          icon: 'school',
        });
      } else if (invitee?.personalData?.certificates?.length) {
        detailList.push({
          label: 'certificates',
          value: invitee.personalData.certificates
            .map((c) => c.displayName)
            .join(', '),
          icon: 'school',
        });
      }

      if (
        user?.role === RoleEnum.POOL &&
        (user as PoolerUser)?.specialities?.length
      ) {
        detailList.push({
          label: 'specialities',
          value: (user as PoolerUser)
            .specialities!.map((s) => s.displayName)
            .join(', '),
          icon: 'menu_book',
        });
      } else if (invitee?.personalData?.specialities?.length) {
        detailList.push({
          label: 'specialities',
          value: invitee.personalData.specialities
            .map((s) => s.displayName)
            .join(', '),
          icon: 'menu_book',
        });
      }

      return detailList;
    }),
    shareReplay(1),
  );

  private changeDetectorTracker?: Tracker.Computation;

  isPooler(user?: User): user is PoolerUser {
    return user?.role === RoleEnum.POOL;
  }

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private toastService: ToastService,
    private poolerService: PoolerService,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private customerService: CustomerService,
  ) {}

  async downloadCV(file?: UploadFile | InviteeFileReference) {
    if (!file) return;
    this.isDownloading$.next(true);
    if ('isExternalFile' in file && file.isExternalFile && file.path) {
      if (file.path.includes('https://vokus24')) {
        file.path = file.path.replace(
          'https://vokus24.',
          'https://api.vokus24.',
        );
      }
      window.open(file.path, '_blank');
      this.isDownloading$.next(false);
    } else {
      downloadFile(file, this.user?._id || '', (percentage: number) => {
        this.progressbarValue$.next(percentage);
      })
        .then(() => {
          this.isDownloading$.next(false);
        })
        .catch(() => {
          this.toastService.add({
            type: 'ERROR',
            message: 'fileNotFound',
          });
          this.isDownloading$.next(false);
        });
    }
  }

  ngOnDestroy() {
    if (this.changeDetectorTracker) {
      this.changeDetectorTracker.stop();
    }
  }
}
